import React, { useState, useRef, useEffect } from 'react'
import { Html5Qrcode } from 'html5-qrcode'
import Drawer from '@components/Drawer/Drawer.component'
import useApplyCarpayVoucher from '@hooks/useApplyCarpayVoucher'
import useCarpayPaymentFlow from '@hooks/useCarpayPaymentFlow'
import { B2COrderResponseFragment } from 'parkdepot-shared/gql/graphql'
import { useTranslation } from 'react-i18next'
import {
    OverlayContainer,
    CodeScannerContainer,
    CodeScannerSubtitle,
    CodeScannerTitle
} from './CodeScannerDrawer.style'
import VoucherSuccess from '@components/Voucher/VoucherSuccess.component'
import { ScannerErrored } from './ScannerErrored.component'
import Spinner from 'parkdepot-shared/components/Spinner'
import { getQrBoxSize, validatePermissionError } from './CodeScanner.utils'

const CodeScannerDrawer = ({
    isOpen,
    onClose,
    orderData
}: {
    isOpen: boolean
    onClose: () => void
    orderData: B2COrderResponseFragment
}) => {
    const { t } = useTranslation()

    const { state } = useCarpayPaymentFlow()

    const qrRef = useRef<HTMLDivElement>(null)

    const [scanner, setScanner] = useState<Html5Qrcode | null>(null)
    const [isIncorrectCode, setIsIncorrectCode] = useState(false)
    const [isPermissionError, setIsPermissionError] = useState(false)

    const stopScanning = async (scannerInstance?: Html5Qrcode) => {
        const qrScanner = scannerInstance || scanner

        if (qrScanner && qrScanner.isScanning) {
            await qrScanner.stop()
            qrScanner.clear()
            setScanner(null)
        }
    }

    const [applyCarpayVoucher, { loading, data: voucherResponseData }] = useApplyCarpayVoucher({
        refetchQueries: ['B2CGetOrderById', 'B2CGetOrderPrice'],
        awaitRefetchQueries: true,
        onError: () => {
            setIsIncorrectCode(true)

            // Give user time to read the error before closing
            setTimeout(() => {
                setIsIncorrectCode(false)
                stopScanning()
                onClose()
            }, 2000)
        },
        onCompleted: () => {
            onClose()
        }
    })

    const handleCodeApply = (qrParsedString: string) => {
        const code = qrParsedString.includes('?c=') ? qrParsedString.split('?c=')[1] : qrParsedString

        applyCarpayVoucher({
            variables: {
                code,
                order_id: orderData.order.order_id,
                selected_parking_tariff: state.parkingTariff,
                parking_context_uuid: state.parkingContextUuid
            }
        })
    }

    const startScanning = async () => {
        if (!qrRef.current) return

        try {
            if (scanner?.isScanning) {
                await stopScanning()
            }

            const scannerInstance = new Html5Qrcode('qr-reader')
            setScanner(scannerInstance)

            await scannerInstance.start(
                { facingMode: 'environment' },
                {
                    fps: 10,
                    qrbox: getQrBoxSize(qrRef)
                },
                (decodedText: string) => {
                    handleCodeApply(decodedText)
                    stopScanning(scannerInstance)
                },
                (error: any) => {
                    console.warn(`QR Code scanning failed:`, error)
                }
            )
        } catch (err) {
            if (validatePermissionError(err)) {
                setIsPermissionError(true)

                // Give user time to read the error before closing
                setTimeout(() => {
                    onClose()
                }, 2000)
            }
        }
    }

    const errorText = isPermissionError
        ? t('carpay.browserScanner.cameraPermission')
        : t('carpay.browserScanner.codeInvalid')

    useEffect(() => {
        let timer: NodeJS.Timeout

        if (isOpen) {
            timer = setTimeout(() => {
                startScanning()
            }, 100)
        }

        return () => {
            setIsPermissionError(false)
            clearTimeout(timer)
            stopScanning()
        }
    }, [isOpen])

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') {
            stopScanning()
            onClose()
        }
    })

    return (
        <Drawer
            isOpen={isOpen}
            onClose={!loading ? onClose : () => null}
            content={
                <>
                    <CodeScannerContainer
                        $loading={loading || !!voucherResponseData || isIncorrectCode || isPermissionError}
                    >
                        {!loading && !isIncorrectCode && !isPermissionError && (
                            <>
                                <CodeScannerTitle>{t('carpay.browserScanner.scan')}</CodeScannerTitle>
                                <CodeScannerSubtitle>
                                    {t('carpay.browserScanner.scannerDescription')}
                                </CodeScannerSubtitle>
                            </>
                        )}
                        <div
                            id="qr-reader"
                            ref={qrRef}
                            style={{
                                width: '100%',
                                maxWidth: '600px',
                                height: '80%',
                                maxHeight: '400px',
                                margin: '0 auto',
                                display: 'block',
                                position: 'relative'
                            }}
                        />
                    </CodeScannerContainer>

                    <OverlayContainer $appearence={!!voucherResponseData}>
                        <VoucherSuccess />
                    </OverlayContainer>

                    <OverlayContainer $appearence={isIncorrectCode || isPermissionError}>
                        <ScannerErrored text={errorText} />
                    </OverlayContainer>

                    <OverlayContainer $appearence={loading}>
                        <Spinner theme="primary" size="xl" />
                    </OverlayContainer>
                </>
            }
        />
    )
}

export { CodeScannerDrawer }
